import { Injectable } from '@angular/core';
import {
  HttpEvent, HttpHandler, HttpInterceptor, HttpRequest,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { OrderFlowTraceService } from './order-flow-trace.service';

/**
 * ApiFailInterceptor
 *
 * Falha de API crítica do funil → `ClientApiCallFailed` (health_15m → alerta
 * api_fail_spike). Endpoint 500/fora do ar é causa-raiz clássica de queda de
 * pedido — e aparece aqui minutos antes de aparecer na conversão.
 *
 * Allowlist por FAMÍLIA (nunca a URL crua: query string carrega dado):
 * só as chamadas cuja falha derruba o funil viram evento. 4xx NÃO reporta —
 * validação de negócio é comportamento, não incidente (o funil já captura
 * rejeição própria via ClientOrderRejected.reason_code).
 *
 * Anti-loop: as URLs de telemetria (order-flow-log / geocode-log) são
 * explicitamente ignoradas — um endpoint de telemetria fora do ar geraria
 * eventos de telemetria sobre a própria falha, em loop.
 */
@Injectable()
export class ApiFailInterceptor implements HttpInterceptor {

  /** família ← padrão da URL (relativa ao apiURL ou absoluta). */
  private static readonly FAMILIES: Array<[RegExp, string]> = [
    [/\/restaurant\/\d+\/order(\/table)?$/, 'order_submit'],
    [/\/restaurant\/deliveryfees/, 'delivery_fee'],
    [/\/restaurant\/\d+\/coupom\/check/, 'coupon'],
    [/\/restaurant\/data\/v2\//, 'menu'],
    [/check-pix-asaas-payment/, 'payment_status'],
  ];

  constructor(private orderFlowTrace: OrderFlowTraceService) { }

  /** Exposto para o spec: null = fora da allowlist (não observar). */
  classify(url: string): string | null {
    try {
      if (!url) return null;
      if (environment.orderFlowLogURL && url.indexOf(environment.orderFlowLogURL) === 0) return null;
      if (environment.multiSignalLogURL && url.indexOf(environment.multiSignalLogURL) === 0) return null;
      if (url.indexOf('order-flow-log') !== -1 || url.indexOf('geocode-log') !== -1) return null;
      // multisignal-log entra na defesa explícita junto com as outras duas. Hoje ele já
      // cairia no null final por não casar nenhuma FAMILY, mas essa proteção é acidental:
      // bastaria alguém adicionar um padrão amplo para o endpoint de telemetria começar a
      // gerar telemetria sobre a própria falha, em loop.
      if (url.indexOf('multisignal-log') !== -1) return null;
      for (const [re, family] of ApiFailInterceptor.FAMILIES) {
        if (re.test(url)) return family;
      }
    } catch (e) { /* classificar nunca pode quebrar a request */ }
    return null;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const family = this.classify(req.url);
    if (!family) return next.handle(req);

    const t0 = Date.now();
    return next.handle(req).pipe(
      tap({
        error: (err: any) => {
          try {
            const status = Number(err && err.status) || 0;
            const isTimeout = !!(err && err.name === 'TimeoutError');
            // Só rede fora (status 0), timeout e 5xx. 4xx é negócio.
            if (!isTimeout && status !== 0 && status < 500) return;
            this.orderFlowTrace.emitHealth('ClientApiCallFailed', {
              endpoint_family: family,
              method: req.method,
              status,
              ms: Date.now() - t0,
              timeout: isTimeout,
            }, 'ApiFailInterceptor.intercept');
          } catch (e) {
            // Telemetria nunca interfere na request original.
          }
        },
      }),
    );
  }
}
